import { UIItem, SectionItem, Section, Row, RowItemType } from '../models';
import { field } from '../core/field-schema';
import { min, max, minLength, maxLength, required, pattern } from '../core/validator-builders';
import { DynamicPhysicalOffsetFieldId } from '../configs/flex/base-config-models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import {
    generateFieldId,
    extractRangeValues,
    extractPrecision,
    calculateFloat2ByteMaxValue,
    createRangeValidators,
    createPrecisionValidator,
    createRangePrecisionCombinedPattern,
    convertToNumeric,
    createParameterizedMessage,
    extractDisplayValueList,
    convertDisplayValueListToOptions,
    extractMinValueFromMinLength,
    NUMERIC_DATA_TYPES,
} from './dynamic-section-field-utils';
import {
    DisplayGroup,
    DynamicDataType,
    DynamicSection,
    DynamicSetting,
    DynamicValidationRuleType,
    SelectOption,
    SensorConfiguration,
    SensorEnablesSettings,
    FlexSettings,
} from '../models/dynamic-section-models';
import { ValidationRule } from '../models/validator-models';
import { FieldItem, FieldType } from '../models/field-models';
import { NormalizedSection } from '../config.models';
import { setFieldValue } from '../actions';

const MP2_PREFIX = 'MP2:';
const PHYSICAL_OFFSET_FIELD = 'physicalOffset';
const SELECT_PREFIX = 'Select ';
const ENTER_PREFIX = 'Enter ';

interface PhysicalOffsetValidationConfig {
    minValue: number;
    maxValue: number;
    decimalPlaces: number;
    precisionMessage: string;
}

const PHYSICAL_OFFSET_NON_METRIC_CONFIG: PhysicalOffsetValidationConfig = {
    minValue: -100000,
    maxValue: 100000,
    decimalPlaces: 2,
    precisionMessage: 'COMMON.OFFSET_NON_METRIC_ERROR',
};

const PHYSICAL_OFFSET_METRIC_CONFIG: PhysicalOffsetValidationConfig = {
    minValue: -2540000,
    maxValue: 2540000,
    decimalPlaces: 1,
    precisionMessage: 'COMMON.OFFSET_METRIC_ERROR',
};

/**
 * Parses a dynamic section configuration into a Section
 * that can be used with the form system.
 *
 * @param dynamicSection The dynamic section configuration
 * @param mp The monitoring point (0 or 1)
 * @param sensorConfig Optional existing sensor configuration values
 * @returns A normalized Section compatible with the form system
 */
export function parseDynamicSection(
    dynamicSection: DynamicSection,
    mp: 0 | 1,
    sensorConfig?: Partial<SensorConfiguration>,
    settings?: { isMetric: boolean; depthUnit: string },
): Section {
    const defaultId = `sensor-${dynamicSection.id.sensorType}`;
    const defaultTitle = dynamicSection.id.name;

    const sensorId = mp === 0 ? `${defaultId}-mp1` : `${defaultId}-mp2`;
    const sensorTitle = mp === 0 ? defaultTitle : `${MP2_PREFIX} ${defaultTitle}`;

    // Create the parent section
    const parentSection: Section = {
        info: {
            id: sensorId,
            title: sensorTitle,
            displayTitle: true,
            expanded: false,
            expandable: true,
            panelClass: 'basic-info-section',
            isDynamic: true,
            metadata: {
                name: dynamicSection.id.name,
                sensorType: dynamicSection.id.sensorType,
                version: dynamicSection.id.version,
            },
        },
        rows: [],
    };

    const physicalOffsetSubsection = createPhysicalOffsetSubsection(
        mp,
        dynamicSection.allowNegativePhysicalOffset,
        sensorConfig,
        settings,
    );
    parentSection.rows.push(createSubsectionRow(`row-physical-offset-${mp}`, physicalOffsetSubsection));

    // Sort display groups by order
    const orderedGroups = [...dynamicSection.displayGroups].sort((a, b) => a.order - b.order);

    // Process each display group
    for (const displayGroup of orderedGroups) {
        // Find all settings for this display group
        const groupSettings = dynamicSection.settings.filter(
            (setting) => setting.displayGroupName === displayGroup.name,
        );

        // Skip empty groups
        if (groupSettings.length === 0) continue;

        // Create subsection for this display group
        const subsection = createSubsection(displayGroup, groupSettings, sensorConfig);

        // Add the subsection to parent section
        parentSection.rows.push(
            createSubsectionRow(`row-${displayGroup.name.toLowerCase().replace(/\s+/g, '-')}`, subsection),
        );
    }

    return parentSection;
}

/**
 * Creates a subsection for a display group
 */
function createSubsection(
    displayGroup: DisplayGroup,
    groupSettings: DynamicSetting[],
    sensorConfig?: Partial<SensorConfiguration>,
): Section {
    const basicSettings = groupSettings.filter((setting) => !setting.isAdvanced);

    const subsection: Section = {
        info: {
            id: `section-${displayGroup.name.toLowerCase().replace(/\s+/g, '-')}`,
            title: displayGroup.name,
            displayTitle: true,
            expanded: false,
            expandable: displayGroup.isToggleable,
            panelClass: 'dynamic-sensor-subsection',
        },
        rows: [],
    };

    // Process settings into rows of two columns each
    const currentRowItems: (FieldItem | UIItem | SectionItem)[] = [];

    for (const setting of basicSettings) {
        const fieldItems = createFieldItems(setting, sensorConfig);

        for (const item of fieldItems) {
            currentRowItems.push(item);

            // Create a new row when we have 2 items
            if (currentRowItems.length === 2) {
                subsection.rows.push({
                    info: {
                        id: `${subsection.info.id}-row-${subsection.rows.length}`,
                        panelClass: 'custom-50p',
                    },
                    items: [...currentRowItems],
                });
                currentRowItems.length = 0; // Clear array while preserving reference
            }
        }
    }

    // Add any remaining items in the last row
    if (currentRowItems.length > 0) {
        subsection.rows.push({
            info: {
                id: `${subsection.info.id}-row-${subsection.rows.length}`,
                panelClass: 'custom-50p',
            },
            items: [...currentRowItems],
        });
    }

    return subsection;
}

function createSubsectionRow(rowId: string, subsection: Section): Row {
    return {
        info: {
            id: rowId,
            panelClass: 'full-width',
        },
        items: [
            {
                id: subsection.info.id,
                type: RowItemType.Section,
                section: subsection,
            },
        ],
    };
}

/**
 * Creates a physical offset subsection for dynamic sensors
 */
function createPhysicalOffsetSubsection(
    mp: 0 | 1,
    allowNegativePhysicalOffset: boolean,
    sensorConfig?: Partial<SensorConfiguration>,
    settings?: { isMetric: boolean; depthUnit: string },
): Section {
    const offsetFieldId =
        mp === 0 ? DynamicPhysicalOffsetFieldId.PhysicalOffset0 : DynamicPhysicalOffsetFieldId.PhysicalOffset1;

    const isMetric = settings?.isMetric ?? false;
    const depthUnit = settings?.depthUnit ?? '';
    const validationConfig = isMetric ? PHYSICAL_OFFSET_METRIC_CONFIG : PHYSICAL_OFFSET_NON_METRIC_CONFIG;
    const minValue = allowNegativePhysicalOffset ? validationConfig.minValue : 0;
    const rangeMessage = createParameterizedMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.RANGE_BETWEEN', {
        min: minValue,
        max: validationConfig.maxValue,
        unit: '',
    });
    const offsetValue = sensorConfig?.physicalOffset
        ? Number(sensorConfig.physicalOffset.toFixed(validationConfig.decimalPlaces))
        : 0;

    const subsection: Section = {
        info: {
            id: `section-physical-offset-${mp}`,
            title: '',
            displayTitle: false,
            expanded: true,
            expandable: false,
            panelClass: '',
        },
        rows: [
            {
                info: {
                    id: `physical-offset-row-${mp}`,
                    panelClass: 'custom-50p',
                },
                items: [
                    field.number(offsetFieldId, PHYSICAL_OFFSET_FIELD, {
                        value: offsetValue,
                        placeholder: 'ADD_EDIT.PHYSICAL_OFFSET',
                        placeholderSuffix: depthUnit,
                        validators: [
                            required({ message: 'LOCATION_CARD_DYNAMIC_TRANSLATION.REQUIRED_FIELD' }),
                            min({
                                value: minValue,
                                message: rangeMessage,
                            }),
                            max({
                                value: validationConfig.maxValue,
                                message: rangeMessage,
                            }),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalOptionalLeadingZeroPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalOptionalLeadingZeroPattern,
                                message: validationConfig.precisionMessage,
                            }),
                        ],
                        decimalPlaces: validationConfig.decimalPlaces,
                        formatDecimalPlaces: true,
                        panelClass: 'each-Item',
                    }),
                ],
            },
        ],
    };

    return subsection;
}

/**
 * Creates a numeric field for UInt16, Float, and Float2Byte data types
 * Consolidates common logic for numeric field creation with validation
 */
function createNumericField(setting: DynamicSetting, currentValue: unknown, unitDisplay: string): FieldItem[] {
    const fieldId = generateFieldId(setting);
    const unitSuffix = setting.unitSuffix ? ` (${setting.unitSuffix})` : '';

    // Check for DisplayValueList first - if present, create select field instead
    const displayValueRule = extractDisplayValueList(setting.validationRules);
    if (displayValueRule) {
        const options = convertDisplayValueListToOptions(displayValueRule);

        const normalizedValue =
            options.find((opt) => String(opt.value) === String(currentValue))?.value ??
            (currentValue as string | number);

        return [
            field.select<{ text: string; value: string | number }, string | number>(fieldId, setting.name, {
                value: normalizedValue,
                label: setting.name + unitSuffix,
                placeholder: SELECT_PREFIX + setting.name + unitSuffix,
                panelClass: 'custom-width',
                optionsSelector: options,
                displayMapper: (option: { text: string; value: string | number }) => option.text,
                valueMapper: (option: { text: string; value: string | number }) => option.value,
                validators: [required({ message: 'LOCATION_CARD_DYNAMIC_TRANSLATION.REQUIRED_FIELD' })],
            }),
        ];
    }

    const validators: ValidationRule[] = [required({ message: 'LOCATION_CARD_DYNAMIC_TRANSLATION.REQUIRED_FIELD' })];

    const { minValue, maxValue } = extractRangeValues(setting.validationRules);
    const minLengthAsMin = extractMinValueFromMinLength(setting.validationRules, setting.dataType);
    const isUInt16 = setting.dataType === DynamicDataType.UInt16;
    const precision = isUInt16 ? 0 : extractPrecision(setting.validationRules, setting.unitType);

    currentValue = roundToPrecision(String(currentValue), precision);

    if (setting.name === FlexSettings.PeakVelocityGain || setting.name === FlexSettings.SurfaceVelocityGain) {
        const formattedPrecision = precision ?? 2;

        if (minValue !== undefined && maxValue !== undefined && precision !== undefined) {
            validators.push(createRangePrecisionCombinedPattern(minValue, maxValue, precision));
        }
        return [
            field.text(fieldId, setting.name, {
                value: formatNumericLike(String(currentValue ?? ''), formattedPrecision),
                label: setting.name + unitSuffix,
                placeholder: ENTER_PREFIX + setting.name + unitDisplay,
                panelClass: 'custom-width',
                validators,
                events: {
                    onChange: (value: string) => [
                        setFieldValue({
                            fieldId,
                            value: formatNumericLike(value, precision ?? 2),
                        }),
                    ],
                },
            }),
        ];
    }

    if (setting.dataType === DynamicDataType.Float2Byte) {
        if (precision !== undefined) {
            const maxAllowedValue = calculateFloat2ByteMaxValue(precision);
            validators.push(createPrecisionValidator(precision));
            validators.push(...createRangeValidators(minValue, maxValue, unitDisplay, maxAllowedValue, minLengthAsMin));
        } else {
            validators.push(...createRangeValidators(minValue, maxValue, unitDisplay, undefined, minLengthAsMin));
        }
    } else if (setting.dataType === DynamicDataType.Float) {
        if (minValue !== undefined && maxValue !== undefined) {
            validators.push(
                ...createRangeValidators(minValue, maxValue, unitDisplay, undefined, minLengthAsMin, precision),
            );
        } else if (precision !== undefined) {
            validators.push(createPrecisionValidator(precision));
        }
    } else {
        validators.push(...createRangeValidators(minValue, maxValue, unitDisplay, undefined, minLengthAsMin));
        if (isUInt16) {
            validators.push(createPrecisionValidator(0));
        }
    }

    const decimalFormatting = getDynamicNumberDecimalFormatting(setting.dataType, precision);

    return [
        field.number(fieldId, setting.name, {
            value: convertToNumeric(currentValue),
            label: setting.name + unitSuffix,
            placeholder: ENTER_PREFIX + setting.name + unitDisplay,
            panelClass: 'custom-width',
            validators,
            ...(isUInt16 ? { integerOnly: true } : {}),
            ...decimalFormatting,
        }),
    ];
}

function getDynamicNumberDecimalFormatting(
    dataType: DynamicDataType,
    precision?: number,
): { decimalPlaces?: number; formatDecimalPlaces?: boolean } {
    if (precision === undefined || (dataType !== DynamicDataType.Float && dataType !== DynamicDataType.Float2Byte)) {
        return {};
    }

    return { decimalPlaces: precision, formatDecimalPlaces: true };
}

function roundToPrecision(value: string, precision?: number): string {
    if (precision === undefined) {
        return value;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(precision) : value;
}

/**
 * Creates field items for a setting based on its data type
 */
function createFieldItems(setting: DynamicSetting, sensorConfig?: Partial<SensorConfiguration>): FieldItem[] {
    // Get current value from sensor config if available
    const currentValue = sensorConfig?.settings?.[setting.name] ?? setting.defaultValue;

    // Extract unit of measure if available in the setting metadata
    const unit = setting.unitType ?? '';
    const unitDisplay = unit ? ` ${unit}` : '';

    const firstRule = setting.validationRules?.[0];

    // For MaskedUInt16 with KeyValuePairs, create multiple select fields
    if (
        setting.dataType === DynamicDataType.MaskedUInt16 &&
        firstRule?.ruleType === DynamicValidationRuleType.KeyValuePairs
    ) {
        const fields: FieldItem[] = [];
        const keyValues = firstRule.values;
        const advancedKeys = firstRule.advancedKeys ?? [];

        // Create a select field for each key in the mask, skipping advanced keys
        for (const key of Object.keys(keyValues)) {
            if (advancedKeys.includes(key)) {
                continue;
            }

            const options = keyValues[key];
            const baseFieldId = generateFieldId(setting);
            const fieldId = `${baseFieldId}-${key.toLowerCase().replace(/\s+/g, '-')}`;

            // Get the value for this key, fallback to first option if not in options list
            let fieldValue: string;
            if (typeof currentValue === 'object' && currentValue !== null) {
                const apiValue = (currentValue as Record<string, string>)[key];
                fieldValue = options.includes(apiValue) ? apiValue : options[0];
            } else {
                fieldValue = options[0];
            }

            fields.push(
                field.select<SelectOption, string>(fieldId, `${setting.name}.${key}`, {
                    value: fieldValue,
                    label: key,
                    placeholder: key,
                    panelClass: 'custom-width',
                    optionsSelector: options.map((v) => ({ text: v, value: v })),
                    displayMapper: (option: SelectOption) => option.text,
                    valueMapper: (option: SelectOption) => option.value,
                    validators: [required({ message: 'LOCATION_CARD_DYNAMIC_TRANSLATION.REQUIRED_FIELD' })],
                }),
            );
        }

        return fields;
    }

    // For MaskedUInt16 with Enum, create a single select field
    if (setting.dataType === DynamicDataType.MaskedUInt16 && firstRule?.ruleType === DynamicValidationRuleType.Enum) {
        if (firstRule.isAdvanced) {
            return [];
        }

        const fieldId = generateFieldId(setting);
        const options = firstRule.values;
        const unitSuffix = setting.unitSuffix ? ` (${setting.unitSuffix})` : '';

        // Fallback to first option if current value is not in the options list
        let enumValue = currentValue as string;
        if (!options.includes(enumValue)) {
            enumValue = options[0] || '';
        }

        return [
            field.select<SelectOption, string>(fieldId, setting.name, {
                value: enumValue,
                label: setting.name + unitSuffix,
                placeholder: setting.name + unitSuffix,
                panelClass: 'custom-width',
                optionsSelector: options.map((v) => ({ text: v, value: v })),
                displayMapper: (option: SelectOption) => option.text,
                valueMapper: (option: SelectOption) => option.value,
                validators: [required({ message: 'LOCATION_CARD_DYNAMIC_TRANSLATION.REQUIRED_FIELD' })],
            }),
        ];
    }

    if (NUMERIC_DATA_TYPES.includes(setting.dataType)) {
        return createNumericField(setting, currentValue, unitDisplay);
    }

    // Default to text field for any unhandled types
    const fieldId = generateFieldId(setting);
    const unitSuffix = setting.unitSuffix ? ` (${setting.unitSuffix})` : '';
    const validators: ValidationRule[] = [required({ message: 'LOCATION_CARD_DYNAMIC_TRANSLATION.REQUIRED_FIELD' })];

    // Add text field validators if defined
    if (setting.validationRules?.length > 0) {
        // Check for minLength/maxLength to possibly join them
        let minLengthValue: number | undefined;
        let maxLengthValue: number | undefined;

        for (const rule of setting.validationRules) {
            if (rule.ruleType === DynamicValidationRuleType.MinLength) {
                minLengthValue = rule.value;
            }
            // Add support for MaxLength if it exists in your model
            if (rule.ruleType === DynamicValidationRuleType.MaxLength) {
                maxLengthValue = rule.value;
            }
        }

        // If both minLength and maxLength are defined, create paired messages
        if (minLengthValue !== undefined && maxLengthValue !== undefined) {
            const lengthMessage = createParameterizedMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.LENGTH_BETWEEN', {
                min: minLengthValue,
                max: maxLengthValue,
            });
            validators.push(
                minLength({
                    value: minLengthValue,
                    message: lengthMessage,
                }),
            );
            validators.push(
                maxLength({
                    value: maxLengthValue,
                    message: lengthMessage,
                }),
            );
        } else {
            // Otherwise create separate validators
            if (minLengthValue !== undefined) {
                validators.push(
                    minLength({
                        value: minLengthValue,
                        message: createParameterizedMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.LENGTH_MIN', {
                            min: minLengthValue,
                        }),
                    }),
                );
            }
            if (maxLengthValue !== undefined) {
                validators.push(
                    maxLength({
                        value: maxLengthValue,
                        message: createParameterizedMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.LENGTH_MAX', {
                            max: maxLengthValue,
                        }),
                    }),
                );
            }
        }
    }

    return [
        field.text(fieldId, setting.name, {
            value: typeof currentValue === 'object' ? JSON.stringify(currentValue) : String(currentValue),
            label: setting.name + unitSuffix,
            placeholder: ENTER_PREFIX + setting.name + unitSuffix,
            panelClass: 'custom-width',
            validators,
        }),
    ];
}

/**
 * Formats raw numeric-like input to the given precision.
 *
 * @param raw       User-entered string value
 * @param precision Number of decimal places to enforce
 */

function formatNumericLike(raw: string, precision: number): string {
    if (!raw) return '';

    if (raw.startsWith('.')) {
        raw = '0' + raw;
    }

    if (raw.endsWith('.')) {
        return raw;
    }

    const num = Number(raw);
    if (isNaN(num)) return '';

    return num.toFixed(precision);
}

/**
 * Process all fields in a normalized section to extract settings
 *
 * @param section The normalized section to process
 * @param settingsObj The settings object to populate
 */
export function processNormalizedSectionFields(
    section: NormalizedSection,
    settingsObj: Record<string, string | number | Record<string, string> | SensorEnablesSettings>,
): void {
    // Track masked settings for later combining
    const maskedSettings: Record<string, Record<string, string>> = {};

    // Process all fields in the normalized section
    Object.keys(section.fields).forEach((fieldId) => {
        const field = section.fields[fieldId];

        // Skip section fields and UI fields
        if (field.type === FieldType.Section || field.type === FieldType.UI) {
            // If it's a nested section, process it recursively
            if (field.type === FieldType.Section) {
                processNormalizedSectionFields(field.section, settingsObj);
            }
            return;
        }

        // Skip fields without a name or value
        if (!field.name || field.value === undefined || field.value === null) {
            return;
        }

        // Skip physical offset fields as they are handled separately
        if (field.name === PHYSICAL_OFFSET_FIELD) {
            return;
        }

        // Check if this is a MaskedUInt16 field with property (name contains dot)
        if (field.name.includes('.')) {
            const [settingName, propertyKey] = field.name.split('.');

            if (!maskedSettings[settingName]) {
                maskedSettings[settingName] = {};
            }

            maskedSettings[settingName][propertyKey] = field.value as string;
        } else {
            // Regular field - convert to appropriate type
            let value: string | number = field.value as string | number;

            // Convert number fields to number type
            if (field.type === FieldType.Number) {
                value = typeof value === 'string' ? parseFloat(value) : value;
            }

            settingsObj[field.name] = value;
        }
    });

    // Add all masked settings to the result
    Object.keys(maskedSettings).forEach((settingName) => {
        settingsObj[settingName] = maskedSettings[settingName];
    });
}
